import React from 'react';
import classNames from 'classnames';
import VoterInfos from 'lib/VoterInfos/VoterInfos';
import PartyBar, { PartyBarType } from 'lib/PartyBar/PartyBar';
import { useTranslation } from 'react-i18next';
import { PartyBarData } from 'lib/PartyBar/partybar.types';

interface Props {
  lists: PartyBarData[];
  currentElectionYear: number;
  prevElectionYear?: number;
  type: PartyBarType;
  isFinal: boolean;
  isMinimal?: boolean;
  eligibleVoters?: number;
  participation?: number;
  numOfSeatsCurrentElection?: number;
  numOfSeatsPrevElection?: number;
  useLegendVotersString?: boolean; // unique case for GRW, where we show "Wählerstimmen" for the canton instead of "Parteistimmen"
  isCantonalTab?: boolean;
  // true for GRW cantonal results where the number of votes is a calculated value
  enforceDecimals?: boolean;
}

const PartyBarList = ({
  lists,
  currentElectionYear,
  prevElectionYear,
  type,
  isFinal,
  isMinimal,
  eligibleVoters,
  participation,
  numOfSeatsCurrentElection,
  numOfSeatsPrevElection,
  useLegendVotersString,
  isCantonalTab,
  enforceDecimals,
}: Props) => {
  const { t } = useTranslation('wap');

  let identifyingKey: 'seatsClaimed' | 'votesRelative';

  switch (type) {
    case 'seats':
      identifyingKey = 'seatsClaimed';
      break;
    case 'votes':
      identifyingKey = 'votesRelative';
      break;
    default:
      break;
  }

  const getMaxValue = (key: string) =>
    Math.max(
      ...lists
        .map((list) => list[key])
        .filter((value) => typeof value === 'number' && !Number.isNaN(value)),
    );

  const maxValueCurrent = getMaxValue(identifyingKey);
  const maxValuePrev = getMaxValue(`${identifyingKey}Prev`);

  const maxValue = maxValuePrev
    ? Math.max(maxValueCurrent, maxValuePrev)
    : maxValueCurrent;

  const hasPrevValues = !lists.every((list) => !list[`${identifyingKey}Prev`]);

  const getSortableValues = (list: PartyBarData) => {
    const numericalValue = parseInt(list.listNumber?.slice(0, 2), 10);
    const letterValue = list.listNumber?.slice(2);
    return { numericalValue, letterValue };
  };

  // sort lists according to their list number which could be 01a, 01b, 02a, 10 etc.
  const getSortedLists = () => {
    return [...lists].sort((a, b) => {
      const { numericalValue: aNumericalValue, letterValue: aLetterValue } =
        getSortableValues(a);
      const { numericalValue: bNumericalValue, letterValue: bLetterValue } =
        getSortableValues(b);

      if (aNumericalValue === bNumericalValue) {
        return aLetterValue.localeCompare(bLetterValue);
      }

      return aNumericalValue - bNumericalValue;
    });
  };

  const sortedLists = getSortedLists();

  const hasLegend = (hasPrevValues && !isMinimal) || type === 'votes';

  // if the number of seats has changed, we show the number of seats for this and the current election in the legend
  const hasNumOfSeatsChanged =
    numOfSeatsCurrentElection &&
    numOfSeatsPrevElection &&
    numOfSeatsCurrentElection !== numOfSeatsPrevElection;

  const seatsPrevElectionString =
    hasNumOfSeatsChanged && ` (${t('seats', { count: numOfSeatsPrevElection })})`;

  const seatsCurrentElectionString =
    hasNumOfSeatsChanged && ` (${t('seats', { count: numOfSeatsCurrentElection })})`;

  const legendText = useLegendVotersString
    ? t('partybar_legend_voters')
    : t('partybar_legend');

  // for cantonal results where count is ongoing, we explain here
  // that we compare with the final results of the prev election.
  // We only need this in the cantonal tab since only here we have preliminary result
  const showPrevIsFinal = !isFinal && isCantonalTab;

  let prevElectionString = `${prevElectionYear}`;
  if (showPrevIsFinal) {
    prevElectionString += ` (${t('result_final_singular')})`;
  }

  return (
    <div className={classNames('partybarlist', { 'partybarlist--nolegend': !hasLegend })}>
      {eligibleVoters && participation && (
        <VoterInfos eligibleVoters={eligibleVoters} participation={participation} />
      )}
      {hasLegend && (
        <div className="partybarlist__legend" aria-hidden>
          {hasPrevValues && !isMinimal && (
            <>
              <span className="partybarlist__legend-bar" />
              {currentElectionYear}
              {seatsCurrentElectionString}
              <span className="partybarlist__legend-bar partybarlist__legend-bar--small" />
              {prevElectionString}
              {seatsPrevElectionString}
            </>
          )}
          {type === 'votes' && (
            <span className="partybarlist__legend-text">{legendText}</span>
          )}
        </div>
      )}
      <ol className="partybarlist__bars">
        {sortedLists?.map((list) => (
          <li key={list.listId} className="partybarlist__bar">
            <PartyBar
              list={list}
              maxValue={maxValue}
              currentElectionYear={currentElectionYear}
              prevElectionYear={prevElectionYear}
              type={type}
              isFinal={isFinal}
              isMinimal={isMinimal}
              hasAnyListPrevValues={hasPrevValues}
              enforceDecimals={enforceDecimals}
            />
          </li>
        ))}
      </ol>
    </div>
  );
};

export default PartyBarList;
